// Shared recharts formatting helpers.
//
// The Dashboard ("This month at a glance") and Analytics tab render the
// same three chart shapes — a bar chart (leads by type) and two donuts
// (by status, by source) — on both pages, using the same [{label, value}]
// data shape. Both pages previously showed raw counts only in tooltips,
// and the donut legends showed bare category names with no number at
// all (had to hover to see anything). This adds each slice/bar's share
// of ITS OWN chart's total inline — percentages are never computed
// across charts (a status slice's % is of the status total, not the
// type total, etc).
//
// Rounds to a whole percent to match the existing convention on the
// Analytics attribution StatBar (e.g. "36%", not "35.7%") — these are
// small counts (typically under 30 leads), so extra decimal precision
// just adds visual noise without adding real information.

export function computeTotal(data, valueKey = "value") {
  return (data || []).reduce((sum, d) => sum + (Number(d[valueKey]) || 0), 0);
}

function pctOf(value, total) {
  if (!total) return 0;
  return Math.round((Number(value) || 0) / total * 100);
}

// Recharts <Tooltip formatter> — pass the same data array the chart
// renders. Keeps the existing name label (e.g. "value", "Closed Won")
// unchanged; only appends "(NN%)" to the displayed value.
export function tooltipPercentFormatter(data, valueKey = "value") {
  const total = computeTotal(data, valueKey);
  return (value, name) => [`${value} (${pctOf(value, total)}%)`, name];
}

// Recharts <Legend formatter> — for Pie legends built from `data` via
// nameKey/dataKey, each entry's `payload.value` holds the original
// numeric value. Renders "Label: N (NN%)" instead of the bare label.
export function legendPercentFormatter(data, valueKey = "value") {
  const total = computeTotal(data, valueKey);
  return (value, entry) => {
    const raw = entry?.payload?.value ?? entry?.payload?.payload?.[valueKey] ?? 0;
    return `${value}: ${raw} (${pctOf(raw, total)}%)`;
  };
}
